import { useEffect, useState } from 'react'
import * as Constants from '@/services/Constants'
import { getAuthTokenFromLocalUserCache } from '@/Logic'
import { env } from './EnvironmentVariables'

type Resource<T> = {
    data: T | null
    loading: boolean
    error: string | null
    refetch: () => void
}

const promises = new Map<string, Promise<any>>()
export function useResource<T = unknown>(url: string, options?: RequestInit): Resource<T> {
    const [data, setData] = useState<T | null>(null)
    const [loading, setLoading] = useState<boolean>(true)

    function load() {
        const opts = buildOptionsWithAuth(options)
        const key = url + opts.method
        setLoading(true)

        if (!promises.has(key)) {
            promises.set(
                key,
                new Promise((resolve, reject) => {
                    fetch(Constants.api_base_path + url, opts)
                        .then((response) => {
                            if (!response.ok) {
                                throw new Error(`HTTP error! status: ${response.status}`)
                            }
                            return response.json() as Promise<T>
                        })
                        .then((json) => {
                            setData(json)
                            resolve(json)
                        })
                        .catch((e) => {
                            setData(null)
                            reject(e)
                        })
                        .finally(() => {
                            setLoading(false)
                            promises.delete(key)
                        })
                })
            )
        }
    }

    useEffect(() => {
        load()
    }, [url, options])

    return { data, loading, error: null, refetch: load }
}

function buildOptionsWithAuth(options?: RequestInit): RequestInit {
    const storedAuthToken = getAuthTokenFromLocalUserCache()

    // Normalize headers to a plain object
    let headers: Record<string, string> = {}

    if (options?.headers) {
        if (options.headers instanceof Headers) {
            options.headers.forEach((value, key) => {
                headers[key] = value
            })
        } else if (Array.isArray(options.headers)) {
            options.headers.forEach(([key, value]) => {
                headers[key] = value
            })
        } else {
            headers = { ...options.headers }
        }
    }

    // Add Authorization if present
    if (storedAuthToken) {
        headers['Authorization'] = 'Bearer ' + storedAuthToken
    }

    // Add X-Client-Version
    headers['X-Client-Version'] = env.APP_VERSION

    return {
        ...options,
        headers,
    }
}
